import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ClearanceDetail } from '../../../../../js/api/models/ClearanceDetail';
import type { PatchedClearanceDetail } from '../../../../../js/api/models/PatchedClearanceDetail';
import { ConnectionsService } from '../../../../../js/api/services/ConnectionsService';
import { handleApiError } from '../../../../../js/app/utils/errorhandling';
import { DrawerField } from '../../../components/drawer-components';
import { InlineDateEdit } from '../../../components/inline-date-edit';
import { StatusCodes } from '../../../constants/workflows';
import { formatDateTo24Hr, parseApiLocalDate, parseFormDate } from '../../../utils/date';
import {
  CLEARANCE_CHANGE_HISTORY_QUERY_KEY,
  CLEARANCE_QUERY_KEY,
  CLEARANCE_STATUS_HISTORY_QUERY_KEY,
  ORGANIZATION_CLEARANCE_LIST_QUERY_KEY,
} from './queryKeys';

function useDebouncedFn<T extends (...args: unknown[]) => void>(fn: T, delayMs: number) {
  const fnRef = useRef(fn);
  fnRef.current = fn;

  return useMemo(() => {
    let t: ReturnType<typeof setTimeout>;
    const debounced = (...args: Parameters<T>) => {
      clearTimeout(t);
      t = setTimeout(() => fnRef.current(...args), delayMs);
    };
    debounced.flush = (...args: Parameters<T>) => {
      clearTimeout(t);
      fnRef.current(...args);
    };
    debounced.cancel = () => clearTimeout(t);
    return debounced as T & { flush: T; cancel: () => void };
  }, [delayMs]);
}

type ClearanceDetailsTabProps = {
  clearance: ClearanceDetail;
};

export function ClearanceDetailsTab({ clearance }: ClearanceDetailsTabProps) {
  const queryClient = useQueryClient();
  const [nextReviewOn, setNextReviewOn] = useState<Date | null>(null);
  const lastSavedNextReviewOnRef = useRef<string | null>(null);
  const isSavingNextReviewOnRef = useRef(false);
  const isNextReviewOnDirtyRef = useRef(false);

  useEffect(() => {
    if (clearance && !isSavingNextReviewOnRef.current) {
      setNextReviewOn(parseApiLocalDate(clearance.next_review_on));
      lastSavedNextReviewOnRef.current = clearance.next_review_on ?? null;
      isNextReviewOnDirtyRef.current = false;
    }
  }, [clearance]);

  const updateNextReviewOnMutation = useMutation({
    mutationFn: async ({ id, value }: { id: string; value: string | null }) => {
      return await ConnectionsService.connectionsClearancePartialUpdate({
        id,
        requestBody: {
          next_review_on: value,
        } as PatchedClearanceDetail,
      });
    },
    onSuccess: async (data: ClearanceDetail, variables) => {
      const { id } = variables;
      queryClient.setQueryData([CLEARANCE_QUERY_KEY, id], data);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [CLEARANCE_QUERY_KEY, id] }),
        queryClient.invalidateQueries({ queryKey: [ORGANIZATION_CLEARANCE_LIST_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [CLEARANCE_STATUS_HISTORY_QUERY_KEY, id] }),
        queryClient.invalidateQueries({ queryKey: [CLEARANCE_CHANGE_HISTORY_QUERY_KEY, id] }),
      ]);
    },
    onError: (error: Error) => {
      handleApiError(error, { messagePrefix: 'Error updating next review date' });
      if (clearance) {
        setNextReviewOn(parseApiLocalDate(clearance.next_review_on));
        lastSavedNextReviewOnRef.current = clearance.next_review_on ?? null;
      }
    },
  });

  const handleNextReviewOnChange = (date: Date | null) => {
    setNextReviewOn(date);
    isNextReviewOnDirtyRef.current = true;
  };

  const nextReviewOnCommit = useCallback(
    async (val?: Date | null) => {
      const dateToSave = val !== undefined ? val : nextReviewOn;
      const dateStr = parseFormDate(dateToSave);

      if (
        dateStr === lastSavedNextReviewOnRef.current ||
        isSavingNextReviewOnRef.current ||
        !isNextReviewOnDirtyRef.current
      ) {
        return;
      }

      try {
        isSavingNextReviewOnRef.current = true;
        await updateNextReviewOnMutation.mutateAsync({
          id: clearance.id,
          value: dateStr,
        });
        lastSavedNextReviewOnRef.current = dateStr;
        isNextReviewOnDirtyRef.current = false;
      } catch (e) {
        console.error('Next review date save failed', e);
      } finally {
        isSavingNextReviewOnRef.current = false;
      }
    },
    [updateNextReviewOnMutation, nextReviewOn, clearance.id]
  );

  const debouncedNextReviewOnCommit = useDebouncedFn(nextReviewOnCommit, 600);

  const handleNextReviewOnBlur = (value?: Date | null) => {
    debouncedNextReviewOnCommit.flush(value);
  };

  const showNextReview =
    clearance.current_status?.code === StatusCodes.NOT_CLEARED ||
    clearance.current_status?.code === StatusCodes.ON_HOLD ||
    clearance.current_status?.code === StatusCodes.RECLEAR;

  return (
    <div className="tw:space-y-4">
      <div className="tw:grid tw:grid-cols-[auto_1fr] tw:gap-x-6 tw:gap-y-2 tw:text-sm">
        {showNextReview && (
          <DrawerField
            label="Next Review"
            value={
              <InlineDateEdit
                value={nextReviewOn}
                onChange={handleNextReviewOnChange}
                onBlur={handleNextReviewOnBlur}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleNextReviewOnBlur(nextReviewOn);
                }}
                placeholder="Set next review date"
                small
                editOn="hover"
                disabled={updateNextReviewOnMutation.isPending}
              />
            }
          />
        )}
        <DrawerField
          label="Created"
          value={
            clearance.created_at ? formatDateTo24Hr(clearance.created_at, 'MM/dd/yyyy HH:mm') : null
          }
        />
        <DrawerField
          label="Last Updated"
          value={
            clearance.updated_at ? formatDateTo24Hr(clearance.updated_at, 'MM/dd/yyyy HH:mm') : null
          }
        />
      </div>
    </div>
  );
}
